import { useState, useEffect, useMemo, useCallback } from "react";

// ════════════════════════════════════════════════════════════════════
// DESIGN TOKENS — copied verbatim from PPCACrashCourse.jsx
// ════════════════════════════════════════════════════════════════════
const C = {
  navy: "#0B1F3A", navy2: "#132d52", gold: "#C9A84C", goldL: "#FDF6E3",
  white: "#FFFFFF", offwhite: "#F8FAFC", slate: "#F1F5F9", border: "#E2E8F0",
  muted: "#64748B", dark: "#1E293B", green: "#059669", greenL: "#ECFDF5",
  blue: "#1D4ED8", blueL: "#EFF6FF", amber: "#B45309", amberL: "#FFFBEB",
  red: "#DC2626", redL: "#FEF2F2", purple: "#7C3AED", purpleL: "#F5F3FF",
};

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const STEPS = [
  { id: "profile", label: "Identity", icon: "👤" },
  { id: "documents", label: "Certifications", icon: "📄" },
  { id: "equipment", label: "Equipment", icon: "🧰" },
  { id: "test", label: "Basic Test", icon: "📝" },
  { id: "status", label: "Status & Badge", icon: "🏅" },
];

const EQUIPMENT_LIST = [
  { key: "amber_light", icon: "🔆", name: "Amber Rotating/Strobe Light", spec: "Visible at minimum 500 ft" },
  { key: "oversize_sign", icon: "🚧", name: "OVERSIZE LOAD Sign", spec: "18\"×84\" min · yellow bg / black letters" },
  { key: "cb_radio", icon: "📻", name: "CB Radio", spec: "Channel 19 default" },
  { key: "flags", icon: "🚩", name: "Warning Flags", spec: "18\"×18\" min · red or orange" },
  { key: "vest_class", icon: "🦺", name: "ANSI Safety Vest", spec: "Class 2 in-vehicle / Class 3 roadside" },
  { key: "fire_extinguisher", icon: "🧯", name: "Fire Extinguisher", spec: "2.5 lb ABC minimum" },
  { key: "first_aid_kit", icon: "⛑️", name: "First Aid Kit", spec: "OSHA Class A" },
];

const TEST_QUESTIONS = [
  { q: "What is the federal maximum legal width before a permit is required?", options: ["8'0\"", "8'6\"", "9'0\"", "10'0\""], correct: 1 },
  { q: "What is the federal gross vehicle weight limit without a permit?", options: ["60,000 lbs", "70,000 lbs", "80,000 lbs", "100,000 lbs"], correct: 2 },
  { q: "Which federal agency sets national size and weight limits?", options: ["FMCSA", "FHWA", "DOT", "NPCA"], correct: 1 },
  { q: "What is the standard operational height clearance used nationwide?", options: ["12'6\"", "13'0\"", "13'6\"", "14'0\""], correct: 2 },
  { q: "When does a load require a permit on Interstate highways?", options: ["Over 8'6\" wide", "Over 13'6\" tall", "Over 80,000 lbs", "All of the above"], correct: 3 },
  { q: "What is the purpose of the Bridge Formula?", options: ["Escort distance", "Axle weight distribution", "Fuel consumption", "Route length"], correct: 1 },
  { q: "Who has authority over oversize permits on state highways?", options: ["Federal government", "Local police", "Each individual state", "NPCA"], correct: 2 },
  { q: "What is the minimum insurance commonly required for escort operations?", options: ["$100,000", "$250,000", "$500,000", "$1,000,000"], correct: 2 },
  { q: "What color must the OVERSIZE LOAD sign be?", options: ["White/Black", "Yellow/Black", "Red/White", "Orange/Black"], correct: 1 },
  { q: "Minimum size of red/orange warning flags?", options: ["12x12", "16x16", "18x18", "24x24"], correct: 2 },
  { q: "What CB channel is the national default for escort operations?", options: ["9", "17", "19", "21"], correct: 2 },
  { q: "What ANSI vest class is required when outside the vehicle on a roadway?", options: ["Class 1", "Class 2", "Class 3", "Any reflective vest"], correct: 2 },
  { q: "Minimum fire extinguisher rating required?", options: ["1 lb", "2.5 lb ABC", "5 lb water", "10 lb CO2"], correct: 1 },
  { q: "What equipment must be covered or removed when not escorting?", options: ["CB radio", "Flags", "OVERSIZE LOAD sign", "Amber lights"], correct: 2 },
  { q: "What is required for communication between lead and rear escorts?", options: ["Cell phone", "CB radio", "Email", "Text messaging"], correct: 1 },
  { q: "Who is responsible for verifying the permitted route before departure?", options: ["Carrier", "Escort operator", "Permit office", "Police"], correct: 1 },
  { q: "What is the primary role of the lead escort?", options: ["Follow the load", "Warn oncoming traffic", "Handle paperwork", "Drive behind the load"], correct: 1 },
  { q: "What is the primary role of the rear escort?", options: ["Clear intersections", "Protect following traffic", "Drive ahead of the load", "Inspect the load"], correct: 1 },
  { q: "When must escort lights be activated?", options: ["Only at night", "Only on highways", "During all permitted moves", "Only when requested"], correct: 2 },
  { q: "Who determines if a move must stop due to weather?", options: ["Carrier", "Escort operator", "Permit office", "Police"], correct: 1 },
  { q: "What should an escort operator do if the load exceeds permitted dimensions?", options: ["Continue", "Call carrier", "Stop and contact permit authority", "Ignore"], correct: 2 },
  { q: "Correct following distance for escorts?", options: ["50–100 ft", "100–200 ft", "200–300 ft", "As required by permit"], correct: 3 },
  { q: "Who has final authority during an escort move?", options: ["Carrier", "Escort operator", "Permit authority", "State police"], correct: 2 },
  { q: "What should be done before entering a bridge?", options: ["Speed up", "Check clearance", "Turn off lights", "Change lanes"], correct: 1 },
  { q: "What is the first step in an emergency?", options: ["Call dispatch", "Stop safely", "Take photos", "Continue to exit"], correct: 1 },
  { q: "When does a permit expire?", options: ["Noon", "Midnight", "6 AM", "5 PM"], correct: 1 },
  { q: "Who must carry a copy of the permit?", options: ["Carrier", "Escort", "Both", "Permit office"], correct: 2 },
  { q: "What happens if a move continues after permit expiration?", options: ["Nothing", "Warning", "Illegal operation", "Auto-renew"], correct: 2 },
  { q: "What must be verified before crossing state borders?", options: ["Fuel", "Weather", "Permits for each state", "Load color"], correct: 2 },
  { q: "Who can legally stop an oversize move?", options: ["Escort operator", "Carrier", "State police", "All of the above"], correct: 3 },
];
const PASS_SCORE = 80;

// ════════════════════════════════════════════════════════════════════
// SERVICE LAYER — "Api" is the ONLY place that talks to storage.
// Every method here is written to mirror a real REST endpoint 1:1.
// Swap the body of each method for a fetch() call and nothing else
// in this file needs to change. The REST contract each one maps to
// is documented in the comment above it.
// ════════════════════════════════════════════════════════════════════
function emptyDriverRecord(driverId) {
  return {
    driver_id: driverId,
    profile: { full_name: "", email: "", phone: "", state: "", dl_number: "", dl_state: "", dl_expiration: "" },
    documents: { id_card: null, npca: null, state_cert: null }, // {name,status,fraud_score,reviewed_by,reviewed_at,uploaded_at}
    equipment: { checklist: {}, photos: { front: null, rear: null } },
    test: { started: false, submitted: false, answers: {}, score: 0, passed: false, attempts: 0, last_attempt: null },
    verification_status: { identity_verified: false, documents_verified: false, equipment_verified: false, basic_test_passed: false, final_status: "pending", updated_at: null },
    badge: { awarded: false, awarded_at: null, badge_id: null },
    created_at: new Date().toISOString(),
  };
}

// Client-side placeholder ONLY. In production this line is deleted and
// replaced by a real call: POST /api/documents/:id/ai-review, which
// runs OCR + a fraud/liveness model server-side (e.g. AWS Textract +
// Rekognition, or a custom CV model) and returns a real fraud_score.
function simulateAiFraudCheck(fileMeta) {
  const nameSignal = /scan|copy|screenshot|fake/i.test(fileMeta.name) ? -0.3 : 0;
  const sizeSignal = fileMeta.size && fileMeta.size < 15000 ? -0.2 : 0.1;
  const base = 0.55 + Math.random() * 0.4;
  const score = Math.max(0, Math.min(1, base + nameSignal + sizeSignal));
  let status = "pending"; // AI unsure -> human review
  if (score >= 0.75) status = "approved";
  else if (score < 0.35) status = "rejected";
  return { fraud_score: Math.round(score * 100) / 100, status };
}

const Api = {
  // GET /api/auth/session -> resolves who's logged in (demo: no password)
  async signIn(driverId, role) {
    return { driver_id: driverId.trim(), role, signed_in_at: new Date().toISOString() };
  },

  // GET /api/drivers/:id
  async getDriver(driverId) {
    try {
      const res = await window.storage.get(`driver:${driverId}`, true);
      return res ? JSON.parse(res.value) : emptyDriverRecord(driverId);
    } catch {
      return emptyDriverRecord(driverId);
    }
  },

  // PUT /api/drivers/:id
  async saveDriver(record) {
    record.verification_status.updated_at = new Date().toISOString();
    await window.storage.set(`driver:${record.driver_id}`, JSON.stringify(record), true);
    return record;
  },

  // GET /api/admin/drivers  (reviewer dashboard — real backend would
  // paginate + auth-check the caller's role server-side)
  async listAllDrivers() {
    try {
      const res = await window.storage.list("driver:", true);
      if (!res || !res.keys) return [];
      const records = await Promise.all(
        res.keys.map(async (k) => {
          try {
            const r = await window.storage.get(k, true);
            return r ? JSON.parse(r.value) : null;
          } catch { return null; }
        })
      );
      return records.filter(Boolean).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    } catch {
      return [];
    }
  },

  // POST /api/documents/:driverId/:docKey  { file }
  // Real version: upload to S3/GCS, kick off async AI review, return job id.
  async uploadDocument(driverId, docKey, file) {
    const driver = await Api.getDriver(driverId);
    const meta = { name: file.name, size: file.size, uploaded_at: new Date().toISOString() };
    const ai = simulateAiFraudCheck(meta);
    const docRecord = { ...meta, ...ai, reviewed_by: "ai", reviewed_at: new Date().toISOString() };
    if (docKey === "id_card") driver.documents.id_card = docRecord;
    else if (docKey === "front" || docKey === "rear") driver.equipment.photos[docKey] = docRecord;
    else driver.documents[docKey] = docRecord;
    return Api.saveDriver(driver);
  },

  // PATCH /api/documents/:driverId/:docKey/review  { decision, reviewer_id }
  async reviewDocument(driverId, docKey, decision, reviewerId) {
    const driver = await Api.getDriver(driverId);
    const patch = { status: decision, reviewed_by: reviewerId || "human", reviewed_at: new Date().toISOString() };
    if (docKey === "id_card") driver.documents.id_card = { ...driver.documents.id_card, ...patch };
    else if (docKey === "front" || docKey === "rear") driver.equipment.photos[docKey] = { ...driver.equipment.photos[docKey], ...patch };
    else driver.documents[docKey] = { ...driver.documents[docKey], ...patch };
    return Api.saveDriver(driver);
  },

  // PUT /api/drivers/:id/profile
  async saveProfile(driverId, profile) {
    const driver = await Api.getDriver(driverId);
    driver.profile = profile;
    return Api.saveDriver(driver);
  },

  // PUT /api/drivers/:id/equipment-checklist
  async saveChecklist(driverId, checklist) {
    const driver = await Api.getDriver(driverId);
    driver.equipment.checklist = checklist;
    return Api.saveDriver(driver);
  },

  // POST /api/drivers/:id/test/submit  { answers }
  async submitTest(driverId, answers) {
    const driver = await Api.getDriver(driverId);
    let correct = 0;
    TEST_QUESTIONS.forEach((q, i) => { if (answers[i] === q.correct) correct++; });
    const score = Math.round((correct / TEST_QUESTIONS.length) * 100);
    const passed = score >= PASS_SCORE;
    driver.test = { started: true, submitted: true, answers, score, passed, attempts: driver.test.attempts + 1, last_attempt: new Date().toISOString() };
    return Api.saveDriver(driver);
  },

  // Internal: recompute verification_status + badge from current record,
  // mirrors the pseudocode:
  //   IF identity_verified AND documents_verified AND equipment_verified
  //      AND basic_test_passed: final_status = "verified" ELSE "pending"
  computeStatus(driver) {
    const identity_verified = !!(driver.profile.full_name && driver.profile.email && driver.profile.phone &&
      driver.profile.state && driver.profile.dl_number && driver.profile.dl_state && driver.profile.dl_expiration &&
      driver.documents.id_card?.status === "approved");
    const documents_verified = driver.documents.npca?.status === "approved" && driver.documents.state_cert?.status === "approved";
    const equipment_verified = EQUIPMENT_LIST.every((e) => driver.equipment.checklist[e.key]) &&
      driver.equipment.photos.front?.status === "approved" && driver.equipment.photos.rear?.status === "approved";
    const basic_test_passed = !!driver.test.passed;
    const final_status = identity_verified && documents_verified && equipment_verified && basic_test_passed ? "verified" : "pending";
    return { identity_verified, documents_verified, equipment_verified, basic_test_passed, final_status };
  },

  // POST /api/drivers/:id/badge/award — server issues badge_id once,
  // idempotent (won't re-issue if already awarded).
  async syncBadge(driverId) {
    const driver = await Api.getDriver(driverId);
    const status = Api.computeStatus(driver);
    driver.verification_status = { ...driver.verification_status, ...status };
    if (status.final_status === "verified" && !driver.badge.awarded) {
      driver.badge = { awarded: true, awarded_at: new Date().toISOString(), badge_id: `PPCA-DRV-${String(Math.floor(100000 + Math.random() * 900000))}` };
    } else if (status.final_status !== "verified" && driver.badge.awarded) {
      driver.badge = { awarded: false, awarded_at: null, badge_id: null };
    }
    return Api.saveDriver(driver);
  },
};

// ════════════════════════════════════════════════════════════════════
// SHARED UI PIECES
// ════════════════════════════════════════════════════════════════════
function SectionEyebrow({ text }) {
  return <div style={{ fontSize: 10.5, fontWeight: 800, color: C.gold, letterSpacing: "0.1em", marginBottom: 12 }}>{text}</div>;
}

function statusColors(status) {
  switch (status) {
    case "approved": case "verified": case "passed":
      return { fg: C.green, bg: C.greenL, label: status === "passed" ? "Passed" : "Approved" };
    case "rejected": case "failed":
      return { fg: C.red, bg: C.redL, label: status === "failed" ? "Failed" : "Rejected" };
    case "pending":
      return { fg: C.amber, bg: C.amberL, label: "Pending Review" };
    default:
      return { fg: C.muted, bg: C.slate, label: "Not Started" };
  }
}

function StatusPill({ status }) {
  const s = statusColors(status);
  return <span style={{ fontSize: 10, fontWeight: 800, color: s.fg, background: s.bg, padding: "3px 9px", borderRadius: 999, letterSpacing: "0.03em", whiteSpace: "nowrap" }}>{s.label}</span>;
}

function Card({ children, style }) {
  return <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, ...style }}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: C.navy }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = { padding: "9px 11px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "inherit", color: C.dark, background: C.white, outline: "none" };
const btnPrimary = (disabled) => ({ padding: "10px 18px", borderRadius: 8, border: "none", background: disabled ? C.border : C.navy, color: disabled ? C.muted : "#fff", fontWeight: 800, fontSize: 12.5, cursor: disabled ? "default" : "pointer", fontFamily: "inherit" });
const btnGhost = { padding: "10px 18px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.navy, fontWeight: 700, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" };

function DemoBanner() {
  return (
    <div style={{ background: C.amberL, borderBottom: `1px solid ${C.amber}33`, padding: "8px 20px", fontSize: 10.5, color: C.amber, textAlign: "center", fontWeight: 700 }}>
      ⚠️ DEMO MODE — data is stored for this artifact only, not a secure production database. Do not enter real personal information.
    </div>
  );
}

function StepTracker({ steps, current, doneMap, onJump }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 22, overflowX: "auto", paddingBottom: 4 }}>
      {steps.map((s, i) => {
        const isCurrent = s.id === current;
        const isDone = doneMap[s.id];
        return (
          <div key={s.id} style={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
            <button onClick={() => onJump(s.id)} style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999,
              border: `1.5px solid ${isCurrent ? C.navy : isDone ? C.green : C.border}`,
              background: isCurrent ? C.navy : isDone ? C.greenL : C.white,
              color: isCurrent ? C.white : isDone ? C.green : C.muted,
              fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all .15s",
            }}>
              <span style={{ fontSize: 13 }}>{isDone && !isCurrent ? "✅" : s.icon}</span>{s.label}
            </button>
            {i < steps.length - 1 && <div style={{ width: 18, height: 1.5, background: isDone ? C.green : C.border, flexShrink: 0 }} />}
          </div>
        );
      })}
    </div>
  );
}

function UploadTile({ title, hint, doc, onFile, onReview, uploading, accept = "image/*,.pdf" }) {
  const status = doc ? doc.status : "none";
  const s = statusColors(status);
  return (
    <div style={{ border: `1.5px dashed ${doc ? s.fg + "55" : C.border}`, borderRadius: 10, padding: 16, background: doc ? s.bg : C.offwhite }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: C.navy }}>{title}</div>
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{hint}</div>
        </div>
        {doc && <StatusPill status={status} />}
      </div>

      {uploading ? (
        <div style={{ fontSize: 11, color: C.muted, padding: "10px 0" }}>Running AI review…</div>
      ) : doc ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 40, height: 40, borderRadius: 6, background: C.slate, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>📎</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: C.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{doc.name}</div>
              <label style={{ fontSize: 10.5, color: C.blue, cursor: "pointer", fontWeight: 700 }}>
                Replace file
                <input type="file" accept={accept} style={{ display: "none" }} onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
              </label>
            </div>
          </div>
          {typeof doc.fraud_score === "number" && (
            <div style={{ marginTop: 8, fontSize: 9.5, color: C.muted }}>
              AI fraud score: <strong style={{ color: doc.fraud_score >= 0.75 ? C.green : doc.fraud_score < 0.35 ? C.red : C.amber }}>{doc.fraud_score}</strong> · reviewed by {doc.reviewed_by}
            </div>
          )}
        </div>
      ) : (
        <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "16px 8px", borderRadius: 8, cursor: "pointer", color: C.muted }}>
          <span style={{ fontSize: 20, marginBottom: 4 }}>⬆️</span>
          <span style={{ fontSize: 11.5, fontWeight: 700 }}>Tap to upload</span>
          <input type="file" accept={accept} style={{ display: "none" }} onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
        </label>
      )}

      {status === "pending" && !uploading && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.border}` }}>
          <div style={{ fontSize: 9.5, color: C.muted, marginBottom: 6, fontStyle: "italic" }}>AI wasn't confident — needs human reviewer sign-off</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => onReview("approved")} style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: "none", background: C.green, color: "#fff", fontWeight: 800, fontSize: 10.5, cursor: "pointer", fontFamily: "inherit" }}>Approve</button>
            <button onClick={() => onReview("rejected")} style={{ flex: 1, padding: "6px 8px", borderRadius: 6, border: "none", background: C.red, color: "#fff", fontWeight: 800, fontSize: 10.5, cursor: "pointer", fontFamily: "inherit" }}>Reject</button>
          </div>
        </div>
      )}
      {status === "rejected" && <div style={{ marginTop: 10, fontSize: 10.5, color: C.red }}>Resubmit a clearer copy to continue.</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 1 — IDENTITY
// ════════════════════════════════════════════════════════════════════
function ProfileStep({ driver, onSaveProfile, onUpload, onReview, uploadingKey, onNext }) {
  const [local, setLocal] = useState(driver.profile);
  useEffect(() => setLocal(driver.profile), [driver.driver_id]);

  const filled = local.full_name && local.email && local.phone && local.state && local.dl_number && local.dl_state && local.dl_expiration;
  const idDoc = driver.documents.id_card;

  return (
    <div>
      <SectionEyebrow text="STEP 1 — IDENTITY VERIFICATION" />
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
          <Field label="Full legal name"><input style={inputStyle} value={local.full_name} onChange={(e) => setLocal({ ...local, full_name: e.target.value })} placeholder="Jane A. Doe" /></Field>
          <Field label="Email"><input style={inputStyle} value={local.email} onChange={(e) => setLocal({ ...local, email: e.target.value })} placeholder="jane@example.com" /></Field>
          <Field label="Phone"><input style={inputStyle} value={local.phone} onChange={(e) => setLocal({ ...local, phone: e.target.value })} placeholder="(555) 555-0100" /></Field>
          <Field label="Home state">
            <select style={inputStyle} value={local.state} onChange={(e) => setLocal({ ...local, state: e.target.value })}>
              <option value="">Select state</option>{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Driver's license number"><input style={inputStyle} value={local.dl_number} onChange={(e) => setLocal({ ...local, dl_number: e.target.value })} placeholder="D1234567" /></Field>
          <Field label="License issuing state">
            <select style={inputStyle} value={local.dl_state} onChange={(e) => setLocal({ ...local, dl_state: e.target.value })}>
              <option value="">Select state</option>{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="License expiration"><input type="date" style={inputStyle} value={local.dl_expiration} onChange={(e) => setLocal({ ...local, dl_expiration: e.target.value })} /></Field>
        </div>
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
          <button onClick={() => onSaveProfile(local)} style={btnGhost}>Save profile</button>
        </div>
      </Card>

      <SectionEyebrow text="GOVERNMENT-ISSUED ID" />
      <div style={{ maxWidth: 340, marginBottom: 18 }}>
        <UploadTile title="Driver's License / State ID" hint="Clear photo, all four corners visible" doc={idDoc}
          uploading={uploadingKey === "id_card"} onFile={(f) => onUpload("id_card", f)} onReview={(d) => onReview("id_card", d)} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button disabled={!filled || idDoc?.status !== "approved"} onClick={onNext} style={btnPrimary(!filled || idDoc?.status !== "approved")}>Continue to Certifications →</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 2 — CERTIFICATIONS
// ════════════════════════════════════════════════════════════════════
function DocumentsStep({ driver, onUpload, onReview, uploadingKey, onNext, onBack }) {
  const bothApproved = driver.documents.npca?.status === "approved" && driver.documents.state_cert?.status === "approved";
  return (
    <div>
      <SectionEyebrow text="STEP 2 — CERTIFICATION UPLOADS" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 14, marginBottom: 18 }}>
        <UploadTile title="NPCA Certificate" hint="National PEVO certification, current and unexpired" doc={driver.documents.npca}
          uploading={uploadingKey === "npca"} onFile={(f) => onUpload("npca", f)} onReview={(d) => onReview("npca", d)} />
        <UploadTile title="State Certification" hint="Your state-issued escort permit or cert, if required" doc={driver.documents.state_cert}
          uploading={uploadingKey === "state_cert"} onFile={(f) => onUpload("state_cert", f)} onReview={(d) => onReview("state_cert", d)} />
      </div>
      <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 8, background: C.slate, marginBottom: 18, fontSize: 10.5, color: C.muted, lineHeight: 1.6 }}>
        🔒 <span>Every upload runs through an AI review (fraud score) the instant it lands. High-confidence uploads auto-approve or auto-reject; anything in between is queued for a human reviewer.</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button onClick={onBack} style={btnGhost}>← Back</button>
        <button disabled={!bothApproved} onClick={onNext} style={btnPrimary(!bothApproved)}>Continue to Equipment →</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 3 — EQUIPMENT
// ════════════════════════════════════════════════════════════════════
function EquipmentStep({ driver, onChecklist, onUpload, onReview, uploadingKey, onNext, onBack }) {
  const checklist = driver.equipment.checklist;
  const allChecked = EQUIPMENT_LIST.every((e) => checklist[e.key]);
  const photosApproved = driver.equipment.photos.front?.status === "approved" && driver.equipment.photos.rear?.status === "approved";

  return (
    <div>
      <SectionEyebrow text="STEP 3 — REQUIRED EQUIPMENT" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 8, marginBottom: 20 }}>
        {EQUIPMENT_LIST.map((e) => (
          <label key={e.key} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", borderRadius: 8, background: checklist[e.key] ? C.greenL : C.white, border: `1px solid ${checklist[e.key] ? C.green + "55" : C.border}`, cursor: "pointer" }}>
            <input type="checkbox" checked={!!checklist[e.key]} onChange={() => onChecklist({ ...checklist, [e.key]: !checklist[e.key] })} style={{ width: 16, height: 16, accentColor: C.green, flexShrink: 0 }} />
            <span style={{ fontSize: 15 }}>{e.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: C.navy }}>{e.name}</div>
              <div style={{ fontSize: 10, color: C.muted }}>{e.spec}</div>
            </div>
          </label>
        ))}
      </div>

      <SectionEyebrow text="VEHICLE PHOTOS" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 14, marginBottom: 18 }}>
        <UploadTile title="Vehicle — Front" hint="Sign, lights, and vest visible" doc={driver.equipment.photos.front}
          uploading={uploadingKey === "front"} onFile={(f) => onUpload("front", f)} onReview={(d) => onReview("front", d)} accept="image/*" />
        <UploadTile title="Vehicle — Rear" hint="Flags and rear lighting visible" doc={driver.equipment.photos.rear}
          uploading={uploadingKey === "rear"} onFile={(f) => onUpload("rear", f)} onReview={(d) => onReview("rear", d)} accept="image/*" />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button onClick={onBack} style={btnGhost}>← Back</button>
        <button disabled={!allChecked || !photosApproved} onClick={onNext} style={btnPrimary(!allChecked || !photosApproved)}>Continue to Basic Test →</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 4 — BASIC TEST
// ════════════════════════════════════════════════════════════════════
function TestStep({ driver, onSubmitTest, onNext, onBack }) {
  const test = driver.test;
  const [answers, setAnswers] = useState({});
  const [active, setActive] = useState(false);
  const allAnswered = Object.keys(answers).length === TEST_QUESTIONS.length;

  if (!active && !test.submitted) {
    return (
      <div>
        <SectionEyebrow text="STEP 4 — PPCA BASIC TEST" />
        <Card>
          <div style={{ fontSize: 15, fontWeight: 900, color: C.navy, marginBottom: 8 }}>Ready to test your baseline knowledge?</div>
          <ul style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.9, paddingLeft: 18, margin: "0 0 16px" }}>
            <li>{TEST_QUESTIONS.length} questions covering federal size, weight, and equipment rules</li>
            <li>Passing score: {PASS_SCORE}% ({Math.ceil(TEST_QUESTIONS.length * PASS_SCORE / 100)} of {TEST_QUESTIONS.length} correct)</li>
            <li>You can retake the test if you don't pass</li>
          </ul>
          {test.attempts > 0 && <div style={{ marginBottom: 14 }}><StatusPill status={test.passed ? "passed" : "failed"} /> <span style={{ fontSize: 11, color: C.muted, marginLeft: 6 }}>Last: {test.score}% · {test.attempts} attempt{test.attempts > 1 ? "s" : ""}</span></div>}
          <button onClick={() => { setAnswers({}); setActive(true); }} style={btnPrimary(false)}>{test.attempts > 0 ? "Retake Test" : "Start Test"}</button>
        </Card>
        <div style={{ marginTop: 16 }}><button onClick={onBack} style={btnGhost}>← Back</button></div>
      </div>
    );
  }

  if (!active && test.submitted) {
    const s = test.passed ? { fg: C.green } : { fg: C.red };
    return (
      <div>
        <SectionEyebrow text="STEP 4 — RESULTS" />
        <Card style={{ textAlign: "center", padding: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 6 }}>{test.passed ? "🎉" : "📚"}</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: s.fg }}>{test.score}%</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginTop: 4 }}>{test.passed ? "You passed the PPCA Basic Test" : "Not quite — you need 80% to pass"}</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>Attempt #{test.attempts}</div>
          <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "center" }}>
            {!test.passed && <button onClick={() => { setAnswers({}); setActive(true); }} style={btnPrimary(false)}>Retake Test</button>}
            {test.passed && <button onClick={onNext} style={{ ...btnPrimary(false), background: C.gold, color: C.navy }}>Continue to Status & Badge →</button>}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <SectionEyebrow text={`STEP 4 — PPCA BASIC TEST · ${Object.keys(answers).length}/${TEST_QUESTIONS.length} ANSWERED`} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
        {TEST_QUESTIONS.map((q, i) => (
          <Card key={i} style={{ padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 8 }}>{i + 1}. {q.q}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 6 }}>
              {q.options.map((opt, j) => (
                <label key={j} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 6, border: `1px solid ${answers[i] === j ? C.navy : C.border}`, background: answers[i] === j ? C.goldL : C.white, cursor: "pointer", fontSize: 11.5 }}>
                  <input type="radio" name={`q${i}`} checked={answers[i] === j} onChange={() => setAnswers({ ...answers, [i]: j })} style={{ accentColor: C.navy }} />{opt}
                </label>
              ))}
            </div>
          </Card>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button onClick={() => setActive(false)} style={btnGhost}>← Cancel</button>
        <button disabled={!allAnswered} onClick={() => { onSubmitTest(answers); setActive(false); }} style={btnPrimary(!allAnswered)}>Submit Test</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 5 — STATUS & BADGE
// ════════════════════════════════════════════════════════════════════
function StatusStep({ driver, onRestart }) {
  const status = driver.verification_status;
  const badge = driver.badge;
  const verified = status.final_status === "verified";
  const doneCount = ["identity_verified", "documents_verified", "equipment_verified", "basic_test_passed"].filter((k) => status[k]).length;
  const pct = Math.round((doneCount / 4) * 100);
  const rows = [
    { label: "Identity Verified", done: status.identity_verified },
    { label: "Certifications Verified", done: status.documents_verified },
    { label: "Equipment Verified", done: status.equipment_verified },
    { label: "PPCA Basic Test Passed", done: status.basic_test_passed },
  ];
  const awardedDate = badge.awarded_at ? new Date(badge.awarded_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;

  return (
    <div>
      <SectionEyebrow text="STEP 5 — STATUS & BADGE" />
      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.navy }}>Verification progress</div>
          <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999, color: verified ? C.green : C.amber, background: verified ? C.greenL : C.amberL }}>{verified ? "VERIFIED" : "PENDING"}</span>
        </div>
        <div style={{ height: 8, borderRadius: 999, background: C.slate, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: verified ? C.green : C.gold, transition: "width .3s" }} />
        </div>
      </Card>

      {verified ? (
        <div style={{ borderRadius: 14, overflow: "hidden", background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 100%)`, border: `1px solid ${C.gold}55` }}>
          <div style={{ padding: "26px 24px 20px", textAlign: "center", borderBottom: `1px solid ${C.gold}33` }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>🏅</div>
            <div style={{ fontSize: 17, fontWeight: 900, color: C.gold, letterSpacing: "0.03em" }}>PPCA VERIFIED DRIVER</div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,.55)", marginTop: 3, letterSpacing: "0.08em", textTransform: "uppercase" }}>Platform-Level Verification</div>
            <div style={{ display: "flex", justifyContent: "center", gap: 18, marginTop: 14, flexWrap: "wrap" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.7)" }}>Verified on <strong style={{ color: C.white }}>{awardedDate}</strong></div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.7)" }}>Badge ID <strong style={{ color: C.white, fontFamily: "monospace" }}>{badge.badge_id}</strong></div>
            </div>
          </div>
          <div style={{ padding: "16px 24px 22px", display: "flex", flexDirection: "column", gap: 9 }}>
            {rows.map((r, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5 }}><span style={{ color: C.gold, fontWeight: 900 }}>✔</span><span style={{ color: "rgba(255,255,255,.9)", fontWeight: 600 }}>{r.label}</span></div>)}
          </div>
          <div style={{ textAlign: "center", padding: "0 24px 22px" }}>
            <span style={{ display: "inline-block", padding: "6px 14px", borderRadius: 999, background: `${C.gold}22`, border: `1px solid ${C.gold}`, color: C.gold, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.05em" }}>✓ VISIBLE ON YOUR PUBLIC PROFILE</span>
          </div>
        </div>
      ) : (
        <div style={{ borderRadius: 14, padding: 28, textAlign: "center", background: C.slate, border: `1px dashed ${C.border}` }}>
          <div style={{ fontSize: 40, marginBottom: 10, filter: "grayscale(1) opacity(0.5)" }}>🔒</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: C.muted }}>PPCA Verified Badge — Locked</div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>Complete verification to unlock your badge.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 16, maxWidth: 260, marginLeft: "auto", marginRight: "auto", textAlign: "left" }}>
            {rows.map((r, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11.5 }}><span>{r.done ? "✅" : "⏳"}</span><span style={{ color: r.done ? C.dark : C.muted, fontWeight: r.done ? 700 : 500 }}>{r.label}</span></div>)}
          </div>
        </div>
      )}
      {verified && <div style={{ marginTop: 18, display: "flex", justifyContent: "center" }}><button onClick={onRestart} style={btnGhost}>Start a new verification (demo reset)</button></div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// DRIVER APP
// ════════════════════════════════════════════════════════════════════
function DriverApp({ session, onLogout }) {
  const [step, setStep] = useState("profile");
  const [driver, setDriver] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);

  const refresh = useCallback(async () => {
    const d = await Api.getDriver(session.driver_id);
    await Api.syncBadge(session.driver_id);
    const synced = await Api.getDriver(session.driver_id);
    setDriver(synced);
  }, [session.driver_id]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!driver) return <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 12.5 }}>Loading your record…</div>;

  const doneMap = {
    profile: driver.verification_status.identity_verified,
    documents: driver.verification_status.documents_verified,
    equipment: driver.verification_status.equipment_verified,
    test: driver.verification_status.basic_test_passed,
    status: driver.verification_status.final_status === "verified",
  };

  const handleUpload = async (docKey, file) => {
    setUploadingKey(docKey);
    await Api.uploadDocument(session.driver_id, docKey, file);
    await refresh();
    setUploadingKey(null);
  };
  const handleReview = async (docKey, decision) => {
    await Api.reviewDocument(session.driver_id, docKey, decision, "self-demo-reviewer");
    await refresh();
  };
  const handleSaveProfile = async (profile) => { await Api.saveProfile(session.driver_id, profile); await refresh(); };
  const handleChecklist = async (checklist) => { await Api.saveChecklist(session.driver_id, checklist); await refresh(); };
  const handleSubmitTest = async (answers) => { await Api.submitTest(session.driver_id, answers); await refresh(); };
  const handleRestart = async () => {
    await Api.saveDriver(emptyDriverRecord(session.driver_id));
    setStep("profile");
    await refresh();
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px clamp(12px, 4vw, 24px) 60px" }}>
      <StepTracker steps={STEPS} current={step} doneMap={doneMap} onJump={setStep} />
      {step === "profile" && <ProfileStep driver={driver} onSaveProfile={handleSaveProfile} onUpload={handleUpload} onReview={handleReview} uploadingKey={uploadingKey} onNext={() => setStep("documents")} />}
      {step === "documents" && <DocumentsStep driver={driver} onUpload={handleUpload} onReview={handleReview} uploadingKey={uploadingKey} onNext={() => setStep("equipment")} onBack={() => setStep("profile")} />}
      {step === "equipment" && <EquipmentStep driver={driver} onChecklist={handleChecklist} onUpload={handleUpload} onReview={handleReview} uploadingKey={uploadingKey} onNext={() => setStep("test")} onBack={() => setStep("documents")} />}
      {step === "test" && <TestStep driver={driver} onSubmitTest={handleSubmitTest} onNext={() => setStep("status")} onBack={() => setStep("equipment")} />}
      {step === "status" && <StatusStep driver={driver} onRestart={handleRestart} />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// REVIEWER DASHBOARD — reads across ALL drivers (shared storage)
// ════════════════════════════════════════════════════════════════════
function ReviewerDashboard({ session, onLogout }) {
  const [drivers, setDrivers] = useState(null);
  const [filter, setFilter] = useState("pending");

  const refresh = useCallback(async () => setDrivers(await Api.listAllDrivers()), []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!drivers) return <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 12.5 }}>Loading driver queue…</div>;

  const pendingDocs = [];
  drivers.forEach((d) => {
    [["id_card", d.documents.id_card, "Identity — ID Card"], ["npca", d.documents.npca, "Certification — NPCA"], ["state_cert", d.documents.state_cert, "Certification — State"], ["front", d.equipment.photos.front, "Equipment — Front Photo"], ["rear", d.equipment.photos.rear, "Equipment — Rear Photo"]]
      .forEach(([key, doc, label]) => {
        if (doc && (filter === "all" || doc.status === filter)) pendingDocs.push({ driverId: d.driver_id, key, doc, label });
      });
  });

  const review = async (driverId, key, decision) => {
    await Api.reviewDocument(driverId, key, decision, session.driver_id);
    await Api.syncBadge(driverId);
    await refresh();
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "24px clamp(12px, 4vw, 24px) 60px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: "0.1em" }}>REVIEWER DASHBOARD</div>
          <div style={{ fontSize: 12, color: C.muted }}>{drivers.length} driver record{drivers.length !== 1 ? "s" : ""} on file</div>
        </div>
        <button onClick={onLogout} style={btnGhost}>Switch account →</button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "7px 14px", borderRadius: 999, border: `1px solid ${filter === f ? C.navy : C.border}`,
            background: filter === f ? C.navy : C.white, color: filter === f ? "#fff" : C.muted,
            fontWeight: 700, fontSize: 11.5, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
          }}>{f}</button>
        ))}
      </div>

      {pendingDocs.length === 0 && <Card style={{ textAlign: "center", color: C.muted, fontSize: 12.5 }}>No documents match this filter.</Card>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {pendingDocs.map((item, i) => (
          <Card key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: C.navy }}>{item.driverId}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{item.label} · {item.doc.name}</div>
              {typeof item.doc.fraud_score === "number" && (
                <div style={{ fontSize: 9.5, color: C.muted, marginTop: 2 }}>
                  AI fraud score: <strong style={{ color: item.doc.fraud_score >= 0.75 ? C.green : item.doc.fraud_score < 0.35 ? C.red : C.amber }}>{item.doc.fraud_score}</strong> · reviewed by {item.doc.reviewed_by}
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <StatusPill status={item.doc.status} />
              {item.doc.status === "pending" && (
                <>
                  <button onClick={() => review(item.driverId, item.key, "approved")} style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: C.green, color: "#fff", fontWeight: 800, fontSize: 10.5, cursor: "pointer", fontFamily: "inherit" }}>Approve</button>
                  <button onClick={() => review(item.driverId, item.key, "rejected")} style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: C.red, color: "#fff", fontWeight: 800, fontSize: 10.5, cursor: "pointer", fontFamily: "inherit" }}>Reject</button>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// LOGIN GATE — demo-only "auth": no password, just identifies a
// driver_id/role so records can be separated. Real auth = a backend
// with hashed passwords + session tokens (see notes).
// ════════════════════════════════════════════════════════════════════
function LoginGate({ onSignIn }) {
  const [driverId, setDriverId] = useState("");
  const [role, setRole] = useState("driver");
  return (
    <div style={{ maxWidth: 380, margin: "60px auto", padding: "0 16px" }}>
      <Card>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: "0.1em", marginBottom: 6 }}>PPCA VERIFICATION CENTER</div>
        <div style={{ fontSize: 16, fontWeight: 900, color: C.navy, marginBottom: 16 }}>Sign in to continue</div>
        <Field label="Driver ID / Employee ID">
          <input style={inputStyle} value={driverId} onChange={(e) => setDriverId(e.target.value)} placeholder="e.g. DRV-1042" />
        </Field>
        <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
          {["driver", "reviewer"].map((r) => (
            <button key={r} onClick={() => setRole(r)} style={{
              flex: 1, padding: "9px 10px", borderRadius: 8, border: `1.5px solid ${role === r ? C.navy : C.border}`,
              background: role === r ? C.navy : C.white, color: role === r ? "#fff" : C.muted, fontWeight: 800, fontSize: 11.5, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
            }}>{r === "driver" ? "👤 Driver" : "🛂 Reviewer"}</button>
          ))}
        </div>
        <button disabled={!driverId.trim()} onClick={() => onSignIn(driverId.trim(), role)} style={{ ...btnPrimary(!driverId.trim()), width: "100%", marginTop: 16 }}>Continue</button>
        <div style={{ fontSize: 9.5, color: C.muted, marginTop: 12, lineHeight: 1.6 }}>Demo sign-in only — no password. A production build needs real authentication behind a server.</div>
      </Card>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ROOT
// ════════════════════════════════════════════════════════════════════
export default function PPCAVerificationTab() {
  const [session, setSession] = useState(null);

  return (
    <div style={{ fontFamily: "'Inter','Helvetica Neue',Arial,sans-serif", background: C.offwhite, minHeight: "100vh", color: C.dark }}>
      <DemoBanner />
      <div style={{ background: `linear-gradient(135deg,${C.navy} 0%,${C.navy2} 100%)`, borderBottom: `1px solid ${C.gold}33`, padding: "26px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.gold, letterSpacing: "0.12em", marginBottom: 8 }}>PPCA VERIFICATION CENTER</div>
          <h1 style={{ fontSize: 23, fontWeight: 900, color: C.white, margin: "0 0 6px", letterSpacing: "-0.03em" }}>
            {session?.role === "reviewer" ? <>Reviewer <span style={{ color: C.gold }}>Dashboard</span></> : <>Get Your <span style={{ color: C.gold }}>Verified Badge</span></>}
          </h1>
          <p style={{ fontSize: 12.5, color: "rgba(255,255,255,.6)", margin: 0, maxWidth: 560, lineHeight: 1.6 }}>
            {session?.role === "reviewer"
              ? "Every document flagged by the AI review queues here for a human decision."
              : "Confirm your identity, upload your certifications, check off your equipment, and pass the basic test to unlock your PPCA Verified badge."}
          </p>
          {session && (
            <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.6)" }}>Signed in as <strong style={{ color: C.white }}>{session.driver_id}</strong> ({session.role})</div>
              <button onClick={() => setSession(null)} style={{ background: "none", border: `1px solid ${C.gold}55`, color: C.gold, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "5px 12px", borderRadius: 6, fontFamily: "inherit" }}>Switch account</button>
            </div>
          )}
        </div>
      </div>

      {!session && <LoginGate onSignIn={(driver_id, role) => setSession({ driver_id, role })} />}
      {session?.role === "driver" && <DriverApp session={session} onLogout={() => setSession(null)} />}
      {session?.role === "reviewer" && <ReviewerDashboard session={session} onLogout={() => setSession(null)} />}
    </div>
  );
}
